import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { APP_RUNTIME_CONFIG } from '@core/auth/app-config.model';

import {
  ChecklistDto,
  ChecklistDueDto,
  ChecklistOverdueLineDto,
  ChecklistScorecardDto,
  CheckoffDto,
  CreateChecklistDto,
  ReplaceChecklistItemsDto,
  UpdateChecklistDto,
} from './checklists.dto';

/**
 * Position checklists — `/api/v1/business-model/checklists`.
 *
 * Two halves of one module, which is why they share a root: the CATALOGUE a
 * checklist is authored in (the Business Model Library tab) and the PER-PERSON
 * record of what somebody owes today. `/certifications/people/{code}/held` is laid
 * out the same way, for the same reason.
 *
 * **A tick is a checkoff.** "Completion" is a banned word in this module, in every
 * layer, so nothing here says it.
 */
@Injectable({ providedIn: 'root' })
export class ChecklistsApiService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(APP_RUNTIME_CONFIG);

  /** Whether a backend is configured. Empty `apiUrl` = the seeded prototype. */
  available(): boolean {
    return !!this.config.apiUrl;
  }

  /** `apiUrl` already carries the /api/v1 prefix — appending another 404s everything. */
  private url(path = ''): string {
    return `${this.config.apiUrl.replace(/\/$/, '')}/business-model/checklists${path}`;
  }

  // ── The catalogue ─────────────────────────────────────────────────────────

  /** Every position checklist, or just the ones a seat carries. */
  listChecklists(positionGuid?: string): Observable<ChecklistDto[]> {
    const params = positionGuid ? new HttpParams().set('positionGuid', positionGuid) : undefined;
    return this.http.get<ChecklistDto[]>(this.url(), { params });
  }

  /** One, with its items in order. */
  readChecklist(checklistGuid: string): Observable<ChecklistDto> {
    return this.http.get<ChecklistDto>(this.url(`/${encodeURIComponent(checklistGuid)}`));
  }

  /**
   * Creates one. **The server issues `reference`** — `CL.1`, `CL.2` … from the same
   * counter that mints `PS.12`, so nothing client-side ever invents one.
   */
  createChecklist(body: CreateChecklistDto): Observable<ChecklistDto> {
    return this.http.post<ChecklistDto>(this.url(), body);
  }

  /**
   * Metadata and assignments. A true patch — only what is sent changes.
   *
   * **Null leaves a list alone; an empty list clears it.** That distinction is the
   * difference between "I did not touch the seats" and "nobody holds this", so the
   * two must not be collapsed by a caller that always sends `[]`.
   */
  updateChecklist(checklistGuid: string, body: UpdateChecklistDto): Observable<ChecklistDto> {
    return this.http.patch<ChecklistDto>(this.url(`/${encodeURIComponent(checklistGuid)}`), body);
  }

  /** Replaces the whole item list. Their order in the array is their order. */
  replaceItems(
    checklistGuid: string,
    body: ReplaceChecklistItemsDto,
  ): Observable<ChecklistDto> {
    return this.http.put<ChecklistDto>(
      this.url(`/${encodeURIComponent(checklistGuid)}/items`),
      body,
    );
  }

  /** Archives it — **ticks point at it**, so it is never actually removed. */
  archiveChecklist(checklistGuid: string): Observable<void> {
    return this.http.delete<void>(this.url(`/${encodeURIComponent(checklistGuid)}`));
  }

  // ── What somebody owes ────────────────────────────────────────────────────
  //
  // ⚠️ THE DUE READ IS FOR TODAY AND LATER ONLY. It answers from the repeat
  // rules and the assignments as they stand NOW, which is the wrong answer for
  // any day already over (§2 "the past comes from the snapshots"). Past days
  // come from the scorecard and overdue reads below, which read the snapshot
  // rows. No screen asks `listDue` / `listDueRange` for a day before today.

  /**
   * One person's day — today or later.
   *
   * Only checklists they owe, and only lines due that day — a checklist with
   * nothing due is left out entirely rather than returned empty, so an empty answer
   * means a clear day.
   *
   * `on` is omitted to mean today **in the zone that person works from**. Computing
   * it here would use the browser's clock: after five in the afternoon a UTC day has
   * already rolled over, and the walk somebody just did would read as missed.
   */
  listDue(personaCode: string, on?: string): Observable<ChecklistDueDto[]> {
    const params = on ? new HttpParams().set('on', on) : undefined;
    return this.http.get<ChecklistDueDto[]>(
      this.url(`/people/${encodeURIComponent(personaCode)}/due`),
      { params },
    );
  }

  /**
   * One person's days across a RANGE — one entry per day, oldest first, in the
   * same shape as a single day.
   *
   * **Both ends or neither.** Defaulting one would turn a typo into a year of
   * reads. Capped at 366 days and refused rather than truncated, so a request
   * that is too wide fails loudly instead of quietly answering less than it was
   * asked for.
   *
   * My Checklists reads today through the next 30 days with it, for My Tasks'
   * Today, This Week and This Month lines. It is never asked for a past day:
   * those come from the snapshot reads.
   */
  listDueRange(personaCode: string, from: string, to: string): Observable<ChecklistDueDto[]> {
    const params = new HttpParams().set('from', from).set('to', to);
    return this.http.get<ChecklistDueDto[]>(
      this.url(`/people/${encodeURIComponent(personaCode)}/due`),
      { params },
    );
  }

  /**
   * One person's checklist scorecard around `asOf`: the DAY, WEEK, MONTH and
   * YEAR spans containing it, overall and per checklist, counted by the backend
   * from the snapshot rows. Omit `asOf` for today **where that person works** —
   * the browser's clock would be a day off for somebody in another zone.
   *
   * A span still running is counted to `asOf`; one wholly past is counted to its
   * own last day, so a past period is asked with that period's last day.
   */
  readScorecard(personaCode: string, asOf?: string): Observable<ChecklistScorecardDto> {
    const params = asOf ? new HttpParams().set('asOf', asOf) : undefined;
    return this.http.get<ChecklistScorecardDto>(
      this.url(`/people/${encodeURIComponent(personaCode)}/scorecard`),
      { params },
    );
  }

  /**
   * What a person missed and still owes: one row per line, its most recent
   * miss, until it is ticked or its next occurrence comes due. Read from the
   * snapshot rows, in the wording that day used. Ticking one is the ordinary
   * {@link checkOff} for its `day`, after which it leaves this read.
   */
  listOverdue(personaCode: string): Observable<ChecklistOverdueLineDto[]> {
    return this.http.get<ChecklistOverdueLineDto[]>(
      this.url(`/people/${encodeURIComponent(personaCode)}/overdue`),
    );
  }

  /**
   * Ticks one line, and answers **the single checkoff just written** — not the day.
   *
   * `on` is the day the line was DUE, not the day it is being recorded: somebody
   * catching up on Monday ticks Friday's walk, and that is the honest record.
   *
   * `personaCode` names whose tick it is, defaulting to the caller — a supervisor
   * closing out for somebody who has gone home is why it can be set.
   *
   * Photos are a **list**, and where the line requires one the tick is refused
   * without it. A site walk can need several, so this takes however many were picked.
   */
  checkOff(
    itemGuid: string,
    opts: { on?: string; personaCode?: string; notes?: string; photos?: File[] } = {},
  ): Observable<CheckoffDto> {
    return this.http.post<CheckoffDto>(
      this.checkoffUrl(itemGuid),
      multipartPhotos(opts.photos),
      { params: checkoffParams(opts) },
    );
  }

  /**
   * Amends a tick that has already been recorded.
   *
   * **An absent `notes` leaves the note alone; a blank one clears it** — the two
   * are different intentions and the shape keeps them apart. A photo is ADDED,
   * never swapped: evidence is not something an edit quietly replaces.
   *
   * It does not weaken `photoRequired`. That rule is about evidence existing at
   * the moment of the claim, which an amendment afterwards cannot satisfy.
   */
  amendCheckoff(
    itemGuid: string,
    opts: { on?: string; personaCode?: string; notes?: string; photos?: File[] } = {},
  ): Observable<CheckoffDto> {
    return this.http.patch<CheckoffDto>(
      this.checkoffUrl(itemGuid),
      multipartPhotos(opts.photos),
      { params: checkoffParams(opts) },
    );
  }

  /**
   * Takes a tick back.
   *
   * **The row and its photos go**, rather than the stamp being cleared: not-done
   * is no row everywhere else here, so a corrected mistake is deliberately
   * indistinguishable from work never recorded.
   *
   * A 404 where there is no tick, which is how a double tap and a stale screen
   * are told apart.
   */
  unCheckOff(
    itemGuid: string,
    opts: { on?: string; personaCode?: string } = {},
  ): Observable<void> {
    return this.http.delete<void>(this.checkoffUrl(itemGuid), { params: checkoffParams(opts) });
  }

  private checkoffUrl(itemGuid: string): string {
    return this.url(`/items/${encodeURIComponent(itemGuid)}/checkoffs`);
  }
}

/**
 * The query a checkoff call carries.
 *
 * `notes` is set whenever it is DEFINED, including when it is empty — on an
 * amendment a blank note means "clear it" and an absent one means "leave it".
 */
function checkoffParams(opts: { on?: string; personaCode?: string; notes?: string }): HttpParams {
  let params = new HttpParams();
  if (opts.on) params = params.set('on', opts.on);
  if (opts.personaCode) params = params.set('personaCode', opts.personaCode);
  if (opts.notes !== undefined) params = params.set('notes', opts.notes);
  return params;
}

/** Photos travel as multipart, several per tick — a site walk can need more than one. */
function multipartPhotos(photos: File[] | undefined): FormData {
  const form = new FormData();
  for (const photo of photos ?? []) form.append('photo', photo, photo.name);
  return form;
}
